import { Link } from 'react-router-dom';
import {
  BookOpen,
  CalendarDays,
  ChevronRight,
  ClipboardCheck,
  Library,
  type LucideIcon,
} from 'lucide-react';

/*
 * A library's front page, the same for Nirvik's (platform admins) and an
 * organization's (its authors): one card per kind of content, each with its
 * own page under `${base}/…`.
 */

type Who = 'nirvik' | 'org';

const SECTIONS: {
  to: string;
  icon: LucideIcon;
  title: string;
  description: Record<Who, string>;
}[] = [
  {
    to: 'courses',
    icon: BookOpen,
    title: 'Courses',
    description: {
      nirvik: 'Self-paced courses. Organizations can take them, and clone them to customize.',
      org: 'Self-paced courses for your members. Assign them, with due dates, from Assignments.',
    },
  },
  {
    to: 'events',
    icon: CalendarDays,
    title: 'Workshops and webinars',
    description: {
      nirvik: 'Live events, scheduled ahead, online, in person or both.',
      org: 'Live events for your members or everyone, online, in person or both.',
    },
  },
  {
    to: 'quizzes',
    icon: ClipboardCheck,
    title: 'Quizzes and assessments',
    description: {
      nirvik: 'Self-assessments, challenges and course assessments.',
      org: 'Self-assessments, challenges and course assessments, with certificates if you like.',
    },
  },
  {
    to: 'question-banks',
    icon: Library,
    title: 'Question banks',
    description: {
      nirvik: 'Questions by topic. Every organization’s authors can reuse them.',
      org: 'Your questions by topic, plus Nirvik’s library banks to reuse.',
    },
  },
];

export function LibraryIndex({ base, who, intro }: { base: string; who: Who; intro: string }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{intro}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {SECTIONS.map(({ to, icon: Icon, title, description }) => (
          <Link
            key={to}
            to={`${base}/${to}`}
            className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 hover:bg-muted"
          >
            <Icon className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="block font-medium">{title}</span>
              <span className="block text-sm text-muted-foreground">{description[who]}</span>
            </span>
            <ChevronRight className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden />
          </Link>
        ))}
      </div>
    </div>
  );
}
